import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Eye, RefreshCw, Search, Edit, Printer, IndianRupee } from 'lucide-react';
import axiosInstance from "../../../src/api/axiosInstance";
import Authenticated from "../../layout/Authenticated";
import Pagination from '../../components/Pagination';
import { FaFileInvoiceDollar } from 'react-icons/fa';

const DebitInvoice = () => {
    const [invoices, setInvoices] = useState([]);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [totalInvoices, setTotalInvoices] = useState(0);

    // Date Range State
    const [dateRange, setDateRange] = useState({
        startDate: '',
        endDate: ''
    });

    // Set default: Last 30 days
    useEffect(() => {
        const today = new Date();
        const last30Days = new Date();
        last30Days.setDate(today.getDate() - 1);

        setDateRange({
            startDate: last30Days.toISOString().split('T')[0],
            endDate: today.toISOString().split('T')[0]
        });
    }, []);

    // Reset to page 1 when search or date changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, dateRange.startDate, dateRange.endDate]);

    const fetchInvoices = async () => {
        setLoading(true);
        setError("");

        try {
            // Backend /invoice/debits doesn't support pagination/search/date params
            // It returns all debits invoices, we'll filter on frontend
            const invoiceResponse = await axiosInstance.get('/invoice/debits');

            const invoiceData = invoiceResponse.data;
            let invoiceList = [];

            if (Array.isArray(invoiceData)) {
                invoiceList = invoiceData;
            } else if (invoiceData && typeof invoiceData === 'object') {
                invoiceList = invoiceData.data || invoiceData.invoices || [];
            }

            // Apply filters on frontend
            let filteredList = invoiceList;

            // Date filter
            if (dateRange.startDate && dateRange.endDate) {
                const startDate = new Date(dateRange.startDate);
                startDate.setHours(0, 0, 0, 0);
                const endDate = new Date(dateRange.endDate);
                endDate.setHours(23, 59, 59, 999);

                filteredList = filteredList.filter(inv => {
                    const invDate = new Date(inv.invoice_date);
                    return invDate >= startDate && invDate <= endDate;
                });
            }

            // Search filter
            if (searchTerm && searchTerm.trim()) {
                const search = searchTerm.toLowerCase();
                filteredList = filteredList.filter(inv =>
                    inv.name?.toLowerCase().includes(search) ||
                    inv.mobile_number?.includes(search) ||
                    inv.village?.toLowerCase().includes(search) ||
                    inv.doctor?.toLowerCase().includes(search) ||
                    inv.id?.toString().includes(search)
                );
            }

            setInvoices(filteredList);
            setTotalInvoices(filteredList.length);

        } catch (err) {
            console.error('Error fetching invoices:', err);
            const msg = err.response?.data?.message || 'Failed to load invoices. Please try again.';
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (dateRange.startDate && dateRange.endDate) {
            fetchInvoices();
        }
    }, [searchTerm, dateRange.startDate, dateRange.endDate]);

    // Print Invoice Function
    const handlePrintInvoice = async (invoiceId) => {
        try {
            const response = await axiosInstance.get(`/invoice/${invoiceId}`);
            const invoiceData = response.data;

            // Create print window
            const printWindow = window.open('', '_blank');

            const printContent = `
                <!DOCTYPE html>
                <html>
                <head>
                    <title>Invoice #${invoiceData.id}</title>
                    <style>
                        * {
                            margin: 0;
                            padding: 0;
                            box-sizing: border-box;
                        }
                        body {
                            font-family: Arial, sans-serif;
                            padding: 20px;
                            font-size: 12px;
                        }
                        .invoice-container {
                            max-width: 800px;
                            margin: 0 auto;
                            border: 2px solid #000;
                            padding: 15px;
                        }
                        .header {
                            text-align: center;
                            border-bottom: 2px solid #000;
                            padding-bottom: 10px;
                            margin-bottom: 15px;
                        }
                        .company-name {
                            font-size: 20px;
                            font-weight: bold;
                            margin-bottom: 5px;
                        }
                        .company-address {
                            font-size: 10px;
                            line-height: 1.4;
                        }
                        .invoice-info {
                            display: flex;
                            justify-content: space-between;
                            margin-bottom: 15px;
                        }
                        .invoice-left, .invoice-right {
                            width: 48%;
                        }
                        .invoice-number {
                            font-weight: bold;
                            margin-bottom: 10px;
                        }
                        .customer-info {
                            line-height: 1.6;
                        }
                        .date-info {
                            text-align: right;
                        }
                        table {
                            width: 100%;
                            border-collapse: collapse;
                            margin-bottom: 15px;
                        }
                        th {
                            background-color: #f0f0f0;
                            border: 1px solid #000;
                            padding: 8px;
                            text-align: left;
                            font-weight: bold;
                        }
                        td {
                            border: 1px solid #000;
                            padding: 8px;
                        }
                        .text-center {
                            text-align: center;
                        }
                        .text-right {
                            text-align: right;
                        }
                        .totals {
                            margin-top: 20px;
                            text-align: right;
                        }
                        .totals-row {
                            display: flex;
                            justify-content: flex-end;
                            margin-bottom: 5px;
                        }
                        .totals-label {
                            width: 150px;
                            text-align: right;
                            padding-right: 20px;
                            font-weight: bold;
                        }
                        .totals-value {
                            width: 100px;
                            text-align: right;
                        }
                        .payment-mode {
                            font-weight: bold;
                            display: flex;
                            justify-content: flex-start;
                            margin-bottom: 5px;
                        }
                        @media print {
                            body {
                                padding: 0;
                            }
                            .invoice-container {
                                border: none;
                            }
                        }
                    </style>
                </head>
                <body>
                    <div class="invoice-container">
                        <div class="header">
                            <div class="company-name">LIFELINE MEDICAL</div>
                            <div class="company-address">
                                2ND FLOOR, SHOP NO.221, NOOR PLAZA, NR. MOMINSHAH BAVA DARGAH<br>
                                OPP. MARKKETING YARD, NH-27, CHANDRAPUR - 363621 | WANKANER, MORBI<br>
                                Lic. GJ-MOR-256412 - GJ-MOR-256413 | Ph: +91 63564 86926
                            </div>
                        </div>

                        <div class="invoice-info">
                            <div class="invoice-left">
                                <div class="invoice-number">Invoice No: ${invoiceData.id}</div>
                                <div class="customer-info">
                                    <div>Name: ${invoiceData.name || 'N/A'}</div>
                                    <div>Mobile: ${invoiceData.mobile_number || 'N/A'}</div>
                                    <div>Doctor: ${invoiceData.doctor || 'N/A'}</div>
                                </div>
                            </div>
                            <div class="invoice-right">
                                <div class="date-info">
                                    <div><strong>Date: ${new Date(invoiceData.invoice_date).toLocaleDateString('en-GB')}</strong></div>
                                    ${invoiceData.village ? `<div style="margin-top: 10px;">Village: ${invoiceData.village}</div>` : ''}
                                </div>
                            </div>
                        </div>

                        <table>
                            <thead>
                                <tr>
                                    <th class="text-center" style="width: 5%;">#</th>
                                    <th style="width: 40%;">Medicine Name</th>
                                    <th class="text-center" style="width: 15%;">Batch</th>
                                    <th class="text-center" style="width: 10%;">Qty</th>
                                    <th class="text-right" style="width: 15%;">Rate</th>
                                    <th class="text-right" style="width: 15%;">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${invoiceData.InvoiceItems?.map((item, index) => `
                                    <tr>
                                        <td class="text-center">${index + 1}</td>
                                        <td>${item.medicines?.name || 'Unknown'}</td>
                                        <td class="text-center">${item.batch_number || 'N/A'}</td>
                                        <td class="text-center">${item.quantity}</td>
                                        <td class="text-right">${parseFloat(item.price_per_unit).toFixed(2)}</td>
                                        <td class="text-right">${parseFloat(item.total_price).toFixed(2)}</td>
                                    </tr>
                                `).join('') || '<tr><td colspan="6" class="text-center">No items</td></tr>'}
                            </tbody>
                        </table>

                        <div class="totals">
                            <div class="totals-row">
                                <div class="totals-label">Sub Total:</div>
                                <div class="totals-value">${parseFloat(invoiceData.total_amount).toFixed(2)}</div>
                            </div>
                            ${invoiceData.round_amount && parseFloat(invoiceData.round_amount) !== 0 ? `
                            <div class="totals-row">
                                <div class="totals-label">Round Off:</div>
                                <div class="totals-value">${parseFloat(invoiceData.round_amount).toFixed(2)}</div>
                            </div>
                            ` : ''}
                            <div class="totals-row" style="border-top: 2px solid #000; padding-top: 5px; margin-top: 5px; display: flex; justify-content: space-between;">
                                <div class="payment-mode" style="margin: 0;">
                                    Payment Mode: ${invoiceData.payment_mode?.toUpperCase() || 'N/A'}
                                </div>
                                <div style="font-weight: bold;">
                                    Total Amount: ₹${parseFloat(invoiceData.net_amount).toFixed(2)}
                                </div>
                            </div>
                        </div>
                    </div>
                    <script>
                        window.onload = function() {
                            window.print();
                        }
                    </script>
                </body>
                </html>
            `;

            printWindow.document.write(printContent);
            printWindow.document.close();
        } catch (err) {
            console.error('Error fetching invoice for print:', err);
            alert('Failed to load invoice for printing. Please try again.');
        }
    };

    const totalPages = Math.ceil(totalInvoices / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage + 1;
    const endIndex = Math.min(startIndex + invoices.length - 1, totalInvoices);

    return (
        <Authenticated>
            <div className="min-h-screen bg-gray-50 py-6 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-center space-x-3">
                            <div className="bg-teal-700 p-3 rounded-xl shadow-md">
                                <IndianRupee className="h-8 w-8 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Debit Invoices</h1>
                                <p className="text-sm text-gray-600">Manage your Pharmacy Debit Invoices</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mt-6">
                        <div className="flex flex-col gap-4">
                            <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between">
                                <div className="relative flex-1 max-w-md">
                                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                                        <Search className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        className="pl-10 w-full py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-200 focus:border-teal-600 transition-all"
                                        placeholder="Search by name, mobile, payment mode..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>

                                <div className="flex items-center gap-2">
                                    <input
                                        type="date"
                                        value={dateRange.startDate}
                                        onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                    />
                                    <span className="text-gray-500 text-sm">to</span>
                                    <input
                                        type="date"
                                        value={dateRange.endDate}
                                        onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                    />
                                </div>

                                <button
                                    onClick={fetchInvoices}
                                    className="flex items-center gap-2 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-all"
                                >
                                    <RefreshCw className="w-4 h-4" />
                                    <span className="hidden md:inline">Refresh</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {error && (
                        <div className="p-4 mx-3 sm:mx-2 bg-red-50 border border-red-200 text-red-700 rounded-xl shadow-sm">
                            <div className="flex items-center gap-2">
                                <span className="font-medium">Error:</span> {error}
                            </div>
                        </div>
                    )}

                    {loading ? (
                        <div className="flex justify-center items-center h-64">
                            <div className="flex flex-col items-center">
                                <RefreshCw className="w-8 h-8 text-teal-600 animate-spin" />
                                <p className="mt-2 text-gray-600">Loading invoices...</p>
                            </div>
                        </div>
                    ) : invoices.length === 0 ? (
                        <div className="bg-white rounded-2xl shadow-md p-10 text-center">
                            <h3 className="text-xl font-bold text-gray-800 mb-2">No invoices found</h3>
                            <p className="text-gray-600">Try adjusting the search or date range.</p>
                        </div>
                    ) : (
                        <div className="bg-white rounded-2xl shadow-2xl border border-teal-200 overflow-hidden mt-7">
                            <div className="overflow-x-auto">
                                <table className="w-full table-auto min-w-max text-xs sm:text-sm">
                                    <thead>
                                        <tr className="bg-teal-700 border-b border-teal-200">
                                            <th className="text-left py-3 sm:py-4 px-3 sm:px-6 font-semibold text-white uppercase tracking-wider">
                                                Customer
                                            </th>
                                            <th className="text-left py-3 sm:py-4 px-3 sm:px-6 font-semibold text-white uppercase tracking-wider">
                                                Mobile
                                            </th>
                                            <th className="text-left py-3 sm:py-4 px-3 sm:px-6 font-semibold text-white uppercase tracking-wider">
                                                Date
                                            </th>
                                            <th className="text-left py-3 sm:py-4 px-3 sm:px-6 font-semibold text-white uppercase tracking-wider">
                                                Amount
                                            </th>
                                            <th className="text-left py-3 sm:py-4 px-3 sm:px-6 font-semibold text-white uppercase tracking-wider">
                                                Payment
                                            </th>
                                            <th className="text-left py-3 sm:py-4 px-3 sm:px-6 font-semibold text-white uppercase tracking-wider">
                                                Items
                                            </th>
                                            <th className="text-left py-3 sm:py-4 px-3 sm:px-6 font-semibold text-white uppercase tracking-wider">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {invoices.map((invoice) => (
                                            <tr
                                                key={invoice.id}
                                                className="border-b border-teal-100 hover:bg-teal-50 transition-all duration-200"
                                            >
                                                <td className="py-3 sm:py-4 px-3 sm:px-6 text-gray-700 font-medium">
                                                    {invoice.name || 'N/A'}
                                                </td>
                                                <td className="py-3 sm:py-4 px-3 sm:px-6 text-gray-700">
                                                    {invoice.mobile_number || 'N/A'}
                                                </td>
                                                <td className="py-3 sm:py-4 px-3 sm:px-6 text-gray-700">
                                                    {new Date(invoice.invoice_date).toLocaleDateString('en-IN')}
                                                </td>
                                                <td className="py-3 sm:py-4 px-3 sm:px-6 text-gray-700 font-semibold">
                                                    ₹{Number(invoice.total_amount || 0).toFixed(2)}
                                                </td>
                                                <td className="py-3 sm:py-4 px-3 sm:px-6 text-gray-700 capitalize">
                                                    {invoice.payment_mode?.replace('_', ' ') || 'N/A'}
                                                </td>
                                                <td className="py-3 sm:py-4 px-3 sm:px-6 text-gray-700 text-xs">
                                                    {invoice.InvoiceItems?.length > 0 ? (
                                                        invoice.InvoiceItems.map((item, idx) => (
                                                            <div key={item.id || idx} className="mb-1 last:mb-0">
                                                                {item.medicines?.name || 'Unknown'}
                                                                <span className="text-gray-500 ml-1">
                                                                    (Qty: {item.quantity}, Batch: {item.batch_number})
                                                                </span>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <span className="text-gray-400">No items</span>
                                                    )}
                                                </td>
                                                <td className="py-3 sm:py-4 px-3 sm:px-6">
                                                    <div className="flex space-x-2">
                                                        <Link
                                                            to={`/view-invoice/${invoice.id}`}
                                                            className="p-2 text-indigo-600 hover:bg-indigo-100 rounded-lg transition-all duration-200 hover:scale-110"
                                                            title="View"
                                                        >
                                                            <Eye size={18} />
                                                        </Link>
                                                        <Link
                                                            to={`/edit-invoice/${invoice.id}`}
                                                            className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-all duration-200 hover:scale-110"
                                                            title="Edit"
                                                        >
                                                            <Edit size={18} />
                                                        </Link>
                                                        <button
                                                            onClick={() => handlePrintInvoice(invoice.id)}
                                                            className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-all duration-200 hover:scale-110"
                                                            title="Print"
                                                        >
                                                            <Printer size={18} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {invoices.length > 0 && (
                                <div className="px-6 py-3 bg-gray-50 text-xs text-gray-500 border-t border-gray-200 flex justify-center items-center">
                                    <div className="text-gray-400">
                                        Date Range: {dateRange.startDate} to {dateRange.endDate}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        itemsPerPage={itemsPerPage}
                        totalItems={totalInvoices}
                        onPageChange={setCurrentPage}
                        startIndex={startIndex}
                        endIndex={endIndex}
                    />
                </div>
            </div>
        </Authenticated>
    );
};

export default DebitInvoice;